import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Input } from '../../../components';
import CustomerContext from '../../../context/customerContext/CustomerContext';

const CreateNewCustomer = ({ closeModal, user }) => {
  const {
    customer,
    setCustomer,
    error,
    addCustomer,
    setError,
    updateCustomer,
  } = useContext(CustomerContext);

  const [logoPreview, setLogoPreview] = useState(
    customer.customerLogoUrl || '',
  );
  const [showPreview, setShowPreview] = useState(false);
  const [imagePath, setImagePath] = useState(customer.customerLogo || '');
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/avif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (file && !allowedTypes.includes(file.type)) {
      setError((prevError) => ({
        ...prevError,
        file: 'Invalid file type. Only .jpg, .jpeg, .png, and .avif files are allowed.',
      }));
      fileInputRef.current.value = null; // Clear the file input
    } else if (file && file.size > maxSize) {
      setError((prevError) => ({
        ...prevError,
        file: 'File size should not exceed 5MB',
      }));
      fileInputRef.current.value = null; // Clear the file input
    } else {
      setError((prevError) => ({
        ...prevError,
        file: null,
      }));
      setCustomer((prevCustomer) => ({
        ...prevCustomer,
        customerLogoFile: file,
      }));
      setLogoPreview(URL.createObjectURL(file)); // Update the logo preview
      setImagePath(file.name); // Update the image path
    }
  };

  useEffect(() => {
    if (user) {
      setCustomer(user);
      setLogoPreview(user.customerLogoUrl || '');
      setImagePath(user.customerLogo || '');
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (previewRef.current && !previewRef.current.contains(event.target)) {
        setShowPreview(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [previewRef]);

  const submitForm = async (customer, e) => {
    e.preventDefault();
    if (customer.id === 0) {
      await addCustomer(customer, e, closeModal);
    } else {
      await updateCustomer(customer, customer.id, e, closeModal);
    }
  };

  return (
    <>
      <form className="" onSubmit={(e) => submitForm(customer, e)}>
        <div className="lg:grid grid-cols-2 gap-5 items-center lg:w-[600px]">
          <div>
            <Input
              disabled={customer?.id}
              name="customerCode"
              type="text"
              label="Customer Code"
              placeholder="Enter Customer Code"
              value={customer.customerCode}
              onChange={handleInputChange}
              error={error?.customerCode}
              required
            ></Input>
          </div>
          <div>
            <Input
              name="customerName"
              type="text"
              label="Customer Name"
              placeholder="Enter Customer Name"
              value={customer.customerName}
              onChange={handleInputChange}
              error={error?.customerName}
              required
            ></Input>
          </div>
          <div>
            <Input
              name="address"
              label="Address"
              type="textarea"
              placeholder="Enter Address"
              value={customer.address}
              onChange={handleInputChange}
            ></Input>
          </div>
          <div>
            <Input
              name="phone"
              label="Phone No."
              type="number"
              placeholder="Enter Phone No."
              value={customer.phone}
              onChange={handleInputChange}
              error={customer.phone && error?.phone}
              errType="valid"
            ></Input>
          </div>
          <div>
            <Input
              type="email"
              name="email"
              label="Email"
              placeholder="Enter Email"
              value={customer.email}
              onChange={handleInputChange}
              error={error?.email}
              required
            ></Input>
          </div>
          <div>
            <Input
              name="avgPurchase"
              label="Average Purchase"
              type="number"
              placeholder="Enter Average Purchase"
              value={customer.avgPurchase}
              onChange={handleInputChange}
              error={error?.avgPurchase}
              required
            ></Input>
          </div>
          <div>
            <Input
              name="contactPerson"
              label="Contact Person"
              placeholder="Enter Contact Person"
              value={customer.contactPerson}
              onChange={handleInputChange}
            ></Input>
          </div>
          <div>
            <Input
              ref={fileInputRef}
              id="fileInput"
              label="Upload Image"
              type="file"
              accept=".jpg,.png,.jpeg,.avif"
              onChange={handleFileChange}
              error={error?.file}
              multiple={false}
            />
          </div>
          <div></div>
          <div className="pt-2 relative">
            {imagePath && (
              <div className="mb-2 text-gray-500 text-sm">{imagePath}</div>
            )}
            {logoPreview && (
              <>
                <span
                  className="text-blue-500 cursor-pointer"
                  onClick={() => setShowPreview(true)}
                >
                  Preview Image
                </span>
                {showPreview && (
                  <div className="fixed inset-0 z-10 flex items-center justify-center bg-opacity-50 lg:pl-72 backdrop-blur-sm">
                    <div
                      ref={previewRef}
                      className="p-4 rounded-lg backdrop-blur-lg"
                    >
                      <img
                        src={logoPreview}
                        alt="Customer Logo"
                        className=" h-96 object-cover"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex justify-start gap-2 mt-10 m-2">
            <Button type="button" className="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="px-4 py-2 bg-yellow-500 text-white rounded-3xl"
            >
              {user ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default CreateNewCustomer;
